'use client';

import type { JSX } from "react";

import { useState } from 'react';

import type { AccordionBlock } from '../types';

import { DescriptionBlocks } from '../DescriptionBlocks';
import styles from './Accordion.module.css';

interface AccordionProps {
  block: AccordionBlock;
  blockId: string;
}

export function Accordion({ block, blockId }: AccordionProps): JSX.Element {
  const allowMultiple = block.allowMultipleOpen ?? true;
  const initial = new Set(block.items.filter((it) => it.defaultOpen).map((it) => it.id));
  const [openIds, setOpenIds] = useState<Set<string>>(initial);

  function toggle(id: string) {
    setOpenIds((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div
      className={styles.wrapper}
      data-cms-block-id={blockId}
      data-cms-field="accordion"
    >
      {block.items.map((item) => {
        const isOpen = openIds.has(item.id);
        return (
          <div key={item.id} className={styles.item}>
            <button
              type="button"
              id={item.id}
              aria-expanded={isOpen}
              aria-controls={`${item.id}-panel`}
              onClick={() => toggle(item.id)}
              className={`${styles.header} ${isOpen ? styles.headerOpen : ''}`}
            >
              <span className={styles.title}>{item.label}</span>
              <span aria-hidden className={styles.chevron} data-open={isOpen} />
            </button>
            <div
              id={`${item.id}-panel`}
              role="region"
              aria-labelledby={item.id}
              hidden={!isOpen}
              className={styles.panel}
            >
              <DescriptionBlocks
                blocks={item.blocks}
                blockIdPrefix={`${blockId}.accordion.${item.id}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
